import { findBestInstagramMediaInShareFields, parseInstagramUrl } from './instagramUrl';

export const PENDING_SHARE_STORAGE_KEY = 'mealPai_pendingShare';

const PENDING_SHARE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface ShareLaunchPayload {
    readonly title?: string;
    readonly text?: string;
    readonly url?: string;
    /** Original location.search captured at share launch — used for diagnostics only. */
    readonly rawSearch?: string;
}

const URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi;

const SPLIT_SHARE_QUERY_KEYS = [
    'igsh',
    'igshid',
    'fbclid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
] as const;

function stripTrailingPunctuation(url: string): string {
    return url.replace(/[.,;:!?)]+$/, '');
}

function collectUrlsFromText(value: string): string[] {
    const matches = value.match(URL_PATTERN) ?? [];
    return matches.map(stripTrailingPunctuation);
}

function repairSplitSharedUrl(search: string, baseUrl: string): string {
    if (!search || !baseUrl) return baseUrl;

    let repaired = baseUrl;
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);

    for (const key of SPLIT_SHARE_QUERY_KEYS) {
        const value = params.get(key);
        if (!value || repaired.includes(`${key}=`)) continue;
        repaired += `${repaired.includes('?') ? '&' : '?'}${key}=${encodeURIComponent(value)}`;
    }

    return repaired;
}

function scoreShareUrl(url: string): number {
    const instagram = parseInstagramUrl(url);
    if (instagram) return 1_000 + url.length;
    if (/instagram\.com|instagr\.am|facebook\.com/i.test(url)) return 500 + url.length;
    return url.length;
}

export function extractShareUrl(payload: ShareLaunchPayload): string | null {
    const instagramMedia = findBestInstagramMediaInShareFields([
        payload.url,
        payload.text,
        payload.title,
    ]);
    if (instagramMedia) {
        return instagramMedia.normalizedUrl;
    }

    const candidates = new Set<string>();

    for (const part of [payload.url, payload.text, payload.title]) {
        if (typeof part !== 'string' || !part.trim()) continue;
        collectUrlsFromText(part).forEach((url) => candidates.add(url));
    }

    if (candidates.size === 0) return null;

    const best = [...candidates].sort((left, right) => scoreShareUrl(right) - scoreShareUrl(left))[0];
    const instagram = parseInstagramUrl(best);
    return instagram?.normalizedUrl ?? best;
}

export function extractShareContextText(payload: ShareLaunchPayload): string {
    return [payload.text, payload.title]
        .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
        .join('\n')
        .trim();
}

export function parseShareLaunchParams(location: Location): ShareLaunchPayload | null {
    const isSharePath = location.pathname === '/share' || location.pathname.endsWith('/share');
    const search = location.search;
    const hasShareParams = /(?:^|[?&])(url|text|title)=/.test(search);

    if (!isSharePath && !hasShareParams) {
        return null;
    }

    const params = new URLSearchParams(search);
    let url = params.get('url') ?? undefined;
    let text = params.get('text') ?? undefined;
    const title = params.get('title') ?? undefined;

    if (url && /instagram\.com|instagr\.am|instagram:\/\//i.test(url)) {
        url = repairSplitSharedUrl(search, url);
    }
    if (text && /instagram\.com|instagr\.am|instagram:\/\//i.test(text)) {
        text = repairSplitSharedUrl(search, text);
    }

    const rawSearchFallback = findBestInstagramMediaInShareFields([search, url, text, title]);
    if (rawSearchFallback && !url) {
        url = rawSearchFallback.normalizedUrl;
    }

    return {
        title,
        text,
        url,
    };
}

interface StoredPendingShare extends ShareLaunchPayload {
    readonly queuedAt: string;
}

export function readPendingShareFromStorage(): ShareLaunchPayload | null {
    try {
        let raw = localStorage.getItem(PENDING_SHARE_STORAGE_KEY);
        if (!raw) {
            raw = sessionStorage.getItem(PENDING_SHARE_STORAGE_KEY);
            if (raw) {
                localStorage.setItem(PENDING_SHARE_STORAGE_KEY, raw);
                sessionStorage.removeItem(PENDING_SHARE_STORAGE_KEY);
            }
        }
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredPendingShare;
        if (typeof parsed !== 'object' || parsed === null) return null;
        if (parsed.queuedAt) {
            const ageMs = Date.now() - new Date(parsed.queuedAt).getTime();
            if (Number.isFinite(ageMs) && ageMs > PENDING_SHARE_MAX_AGE_MS) {
                localStorage.removeItem(PENDING_SHARE_STORAGE_KEY);
                return null;
            }
        }
        return {
            title: parsed.title,
            text: parsed.text,
            url: parsed.url,
            rawSearch: parsed.rawSearch,
        };
    } catch {
        return null;
    }
}

export function writePendingShareToStorage(payload: ShareLaunchPayload): void {
    const stored: StoredPendingShare = {
        ...payload,
        queuedAt: new Date().toISOString(),
    };
    localStorage.setItem(PENDING_SHARE_STORAGE_KEY, JSON.stringify(stored));
}

export function clearPendingShareFromStorage(): void {
    localStorage.removeItem(PENDING_SHARE_STORAGE_KEY);
}

export function clearShareLaunchFromHistory(): void {
    window.history.replaceState({}, '', `${window.location.origin}/`);
}

/** Persist share params from the current URL; safe to call repeatedly. */
export function captureShareLaunchFromLocation(location: Location = window.location): boolean {
    const payload = parseShareLaunchParams(location);
    if (!payload) return false;
    writePendingShareToStorage({
        ...payload,
        rawSearch: location.search,
    });
    clearShareLaunchFromHistory();
    return true;
}
